// The API catalogue, the consumer keys and the app's catalog on the Lender Hub API (LH-B4, api-contract.md §5):
//   GET /catalogue                       the rows the screen shows (the Hub computes them, as urls.ts does here)
//   GET /catalogue/postman · /postman/{uat|production} · /openapi.json   the download files
//   GET /consumers · POST /consumers/{website|app}/replace   the keys (masked; a new one is shown once)
//   GET /app-catalog                     what the web app gets now from /service/v1/app/catalog, and why each lender is in or not
// On the fixtures (npm test) the same view is made from the stores with urls.ts.
import { api } from '@/lib/hubApi'
import { effectiveState, type EndpointState } from '@/lib/flow'
import { catalogueRows, openApiFor, postmanCollection, postmanEnvironment, shownBatch, type HubEnv } from '@/lib/urls'
import type { Batch, Lender, ManifestEndpoint, PortalSettings, ServiceEndpoint } from '@/types'

export interface CatalogueView {
  urls: PortalSettings['hubUrls']
  lenders: { slug: string; name: string; status: Lender['status']; batch: { version: string; status: Batch['status'] } | null }[]
  rows: { lenderSlug: string; endpoint: ManifestEndpoint; uat: string; production: string | null }[]
  serviceEndpoints: { id: string; lenderSlug: string; productSlug: string; env: ServiceEndpoint['env']; state: EndpointState; workflowName: string; workflowVersion: string }[]
}

/** The view from this browser's stores (the fixtures). */
export function catalogueFromStores(lenders: Lender[], batches: Batch[], endpoints: ServiceEndpoint[], urls: PortalSettings['hubUrls']): CatalogueView {
  return {
    urls,
    lenders: lenders.map((l) => {
      const b = shownBatch(batches, l.slug)
      return { slug: l.slug, name: l.name, status: l.status, batch: b ? { version: b.version, status: b.status } : null }
    }),
    rows: catalogueRows(lenders, batches, urls).map((r) => ({ lenderSlug: r.lender.slug, endpoint: r.endpoint, uat: r.uat, production: r.production })),
    serviceEndpoints: endpoints.filter((e) => e.state !== 'retired').map((e) => {
      const lender = lenders.find((l) => l.slug === e.lenderSlug)
      return { id: e.id, lenderSlug: e.lenderSlug, productSlug: e.productSlug, env: e.env, state: effectiveState(e, lender, lender?.products.find((p) => p.slug === e.productSlug)), workflowName: e.workflowName, workflowVersion: e.workflowVersion }
    }),
  }
}

interface HubCatalogue {
  urls: PortalSettings['hubUrls']
  lenders: { slug: string; name: string; status: Lender['status']; batch: { version: string; status: Batch['status'] } | null }[]
  rows: { lender: { slug: string }; endpoint: ManifestEndpoint; uat: string; production: string | null }[]
  serviceEndpoints: CatalogueView['serviceEndpoints']
}

/** The view as the Hub computes it. */
export async function catalogueFromHub(): Promise<CatalogueView> {
  const c = await api<HubCatalogue>('GET', '/catalogue')
  return { urls: c.urls, lenders: c.lenders.map((l) => ({ slug: l.slug, name: l.name, status: l.status, batch: l.batch ? { version: l.batch.version, status: l.batch.status } : null })), rows: c.rows.map((r) => ({ lenderSlug: r.lender.slug, endpoint: r.endpoint, uat: r.uat, production: r.production })), serviceEndpoints: c.serviceEndpoints }
}

export type CatalogueFile = 'postman' | 'postman-uat' | 'postman-production' | 'openapi'
const HUB_PATH: Record<CatalogueFile, string> = { postman: '/catalogue/postman', 'postman-uat': '/catalogue/postman/uat', 'postman-production': '/catalogue/postman/production', openapi: '/catalogue/openapi.json' }

/** A download file from the Hub. */
export const catalogueFileFromHub = (file: CatalogueFile) => api<unknown>('GET', HUB_PATH[file])

/** The same file made here from the stores (the fixtures). */
export function catalogueFileFromStores(file: CatalogueFile, lenders: Lender[], batches: Batch[], endpoints: ServiceEndpoint[], urls: PortalSettings['hubUrls'], applicant: Record<string, unknown>): unknown {
  if (file === 'postman') return postmanCollection(lenders, batches, endpoints, applicant)
  if (file === 'openapi') return openApiFor(lenders, batches, endpoints, urls)
  return postmanEnvironment(file.replace('postman-', '') as HubEnv, urls)
}

// ---- the app's catalog ----

export interface AppStanding { slug: string; name: string; inApp: boolean; publishedAt: string | null; publishedVersion: number | null; why: string | null }
export interface AppCatalogView { catalog: { version: string; generated_at: string; mode: { lenders_live: boolean; live_count: number; force_prelaunch: boolean }; lenders: Record<string, unknown>[] }; lenders: AppStanding[] }

export const getAppCatalog = () => api<AppCatalogView>('GET', '/app-catalog')
